import {
  Box,
  Center,
  HStack,
  Link,
  LinkProps,
  Menu,
  MenuDivider,
  MenuGroup,
  MenuItem,
} from '@hover/blueprint';
import { Link as ChakraLink } from '@hover/blueprint/chakra';
import { motion } from '@hover/blueprint/foundation';
import { Link as GatsbyLink } from 'gatsby';
import { transitions } from 'polished';
import React, {
  Dispatch,
  FunctionComponent,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { HoverLogo, HoverLogoWhite } from '../../../icons';
import { INavbarItem } from '../../../interfaces/INavbarItems';
import { HEADER_HEIGHT } from '../Layout/Layout';
import NavbarDownloadAppButton from './NavbarDownloadAppButton';
import NavbarLink from './NavbarLink';

export interface NavbarDesktopProps {
  links: INavbarItem[];
  isNavbarWhite: boolean;
  navbarScrolled: boolean;
  showDivider: boolean;
  dividerColor?: string;
  showShadow?: boolean;
  setShowShadow: Dispatch<SetStateAction<boolean>>;
  buttonType: string;
}

type MouseEnterHandlerFactory = (label: string) => LinkProps['onMouseEnter'];

/**
 * Safari renders rgba(0, 0, 0, 0) for transparent 😠
 */
const transparent = 'rgba(255, 255, 255, 0)';

const MENU_CLOSE_DELAY = 250;

const NavbarDesktop: FunctionComponent<NavbarDesktopProps> = ({
  links,
  isNavbarWhite,
  navbarScrolled,
  showDivider,
  dividerColor,
  showShadow,
  setShowShadow,
  buttonType,
}) => {
  const [isOverTrigger, setIsOverTrigger] = useState(false);
  const [isOverMenu, setIsOverMenu] = useState(false);

  const [activeLink, setActiveLink] = useState<string | null>(null);

  const closeMenu = () => setActiveLink(null);

  const onTriggerMouseLeave = () => setIsOverTrigger(false);
  const triggerMouseEnterHandlerFor: MouseEnterHandlerFactory = label => () => {
    setIsOverTrigger(true);
    setActiveLink(label);
  };

  const onMenuMouseLeave = () => setIsOverMenu(false);
  const onMenuMouseEnter = () => setIsOverMenu(true);
  const menuOpenHandlerFor = (label: string) => () => setActiveLink(label);

  const onMenuClose = () => closeMenu();
  const onLinkMouseEnter = () => closeMenu();

  useEffect(() => {
    if (!isOverTrigger && !isOverMenu) {
      const id = setTimeout(closeMenu, MENU_CLOSE_DELAY);

      return () => clearTimeout(id);
    }

    return undefined;
  }, [isOverTrigger, isOverMenu]);

  useEffect(() => {
    if ((isOverTrigger || isOverMenu) && !navbarScrolled && isNavbarWhite) {
      setShowShadow(true);
    } else if (showShadow) {
      const id = setTimeout(() => setShowShadow(false), MENU_CLOSE_DELAY);

      return () => clearTimeout(id);
    }

    return undefined;
  }, [
    isOverTrigger,
    isOverMenu,
    navbarScrolled,
    setShowShadow,
    showDivider,
    showShadow,
    isNavbarWhite,
  ]);

  const getBorderColor = () => {
    if (!showDivider || navbarScrolled || showShadow) return 'transparent';
    if (isNavbarWhite) return dividerColor;

    return 'neutral.0';
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      width="100%"
      borderColor={getBorderColor()}
      borderWidth={0}
      borderStyle="solid"
      borderBottomWidth={400}
      {...transitions(['border-color'], motion.easings[250])}
    >
      <Box
        display="flex"
        alignItems="center"
        height={HEADER_HEIGHT}
        justifyContent="space-between"
        pl={200}
        pr={300}
      >
        <Link as={GatsbyLink} to={`${process.env.GATSBY_V2_PREFIX}/`}>
          {isNavbarWhite ? (
            <HoverLogo
              title="HOVER"
              alt="HOVER Logo"
              style={{ width: 147, height: 36 }}
            />
          ) : (
            <HoverLogoWhite
              title="HOVER"
              alt="HOVER Logo white"
              style={{ width: 147, height: 36 }}
            />
          )}
        </Link>
        <Center height="100%">
          <HStack spacing={12} height="100%">
            {links.map(link =>
              link.children ? (
                <Menu
                  colorScheme="primary"
                  borderTopRadius={0}
                  variant="navigation"
                  data-label={link.label}
                  key={link.label}
                  isOpen={activeLink === link.label}
                  // NOTE(@jrolfs): this thing "joins" the menu to the navbar
                  // with a dividing line (based off of the navbar divider
                  // color when it is visible) and a little gradient that
                  // covers the top part of the menu's shadow in some cases
                  _after={{
                    ...transitions(['background', motion.easings[250]]),
                    background: `linear-gradient(${transparent}, ${
                      isNavbarWhite || navbarScrolled || !showDivider
                        ? '#fff'
                        : transparent
                    })`,
                    borderBottomColor: 'neutral.100',
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    content: '""',
                    height: '10px',
                    left: 0,
                    position: 'absolute',
                    right: 0,
                    top: '-9px',
                  }}
                  position="relative"
                  onClose={onMenuClose}
                  onOpen={menuOpenHandlerFor(link.label)}
                  marginTop="-7px"
                  trigger={
                    <NavbarLink
                      isLight={!isNavbarWhite}
                      onMouseEnter={triggerMouseEnterHandlerFor(link.label)}
                      onMouseLeave={onTriggerMouseLeave}
                    >
                      {link.label}
                    </NavbarLink>
                  }
                >
                  <Box
                    flexDirection="column"
                    onMouseEnter={onMenuMouseEnter}
                    onMouseLeave={onMenuMouseLeave}
                    zIndex="dropdown"
                  >
                    {link.children
                      ? link.children.map((childLink, index) =>
                          childLink.children ? (
                            <MenuGroup
                              mb={600}
                              title={childLink.label}
                              key={childLink.label}
                              cursor="pointer"
                            >
                              {childLink.children.map(({ label, to }) => (
                                <MenuItem
                                  as={GatsbyLink}
                                  to={to ?? ''}
                                  key={label}
                                >
                                  {label}
                                </MenuItem>
                              ))}
                              {childLink.children &&
                              link.children &&
                              index !== link.children?.length - 1 ? (
                                <MenuDivider />
                              ) : null}
                            </MenuGroup>
                          ) : (
                            <MenuItem
                              as={GatsbyLink}
                              to={childLink.to ?? ''}
                              key={childLink.label}
                            >
                              {childLink.label}
                            </MenuItem>
                          ),
                        )
                      : null}
                  </Box>
                </Menu>
              ) : (
                <NavbarLink
                  as={GatsbyLink}
                  to={link.to ?? ''}
                  key={link.to}
                  isLight={!isNavbarWhite}
                  onMouseEnter={onLinkMouseEnter}
                >
                  {link.label}
                </NavbarLink>
              ),
            )}
          </HStack>
        </Center>
        <Box>
          <HStack spacing={8}>
            <NavbarDownloadAppButton buttonType={buttonType} />
            <ChakraLink
              _hover={{ color: isNavbarWhite ? 'primary.600' : 'primary.200' }}
              href={`${process.env.GATSBY_V2_PREFIX}/login`}
              color={isNavbarWhite ? undefined : 'neutral.0'}
            >
              Log in
            </ChakraLink>
          </HStack>
        </Box>
      </Box>
    </Box>
  );
};

export default NavbarDesktop;
